import { dateKey, defaultCycleAnchor, parseAnchor } from './state';
import { planDayFor } from './logic/schedule';

export function countCompletedSets(dayLog) {
  return Object.values(dayLog || {}).reduce((t, sets) => t + (sets || []).filter(s => s?.done).length, 0);
}
/* Only done sets count: Copy prefills unticked rows, which are not work yet. */
export function sumDayVolume(dayLog) {
  return Object.values(dayLog || {}).reduce(
    (t, sets) => t + (sets || []).reduce(
      (s, x) => s + (x?.done ? (Number(x.weight) || 0) * (Number(x.reps) || 0) : 0), 0,
    ), 0,
  );
}
export function hasSessionData(dayLog) { return countCompletedSets(dayLog) > 0; }

/* The last session of an exercise, with EVERY completed set — the shape of
 * the work, not just its heaviest moment. A top-set summary ("prev 80kg x 8")
 * can't tell 80x8,8,8 apart from 80x8,5,3, which is exactly the difference
 * between repeating a session and overreaching on it.
 *
 * When plan context is supplied it prefers the last session of the SAME plan
 * day — "last Push A" — because an exercise that appears on two days would
 * otherwise show you the other day's numbers. Falls back to the most recent
 * session containing the exercise. */
export function findPreviousSession(logs, currentDateKey, exerciseName, planCtx) {
  const dates = Object.keys(logs || {})
    .filter(k => k < currentDateKey)
    .sort((a, b) => b.localeCompare(a));

  const sessionAt = (k) => {
    const done = (logs[k]?.[exerciseName] || []).filter(s => s?.done);
    if (!done.length) return null;
    return {
      date: k,
      sets: done.map(s => ({ weight: Number(s.weight) || 0, reps: Number(s.reps) || 0 })),
    };
  };

  // planCtx { state, planDayId }: prefer the last session of the same plan
  // day, by its sessions stamp first, so a cycle change never mixes days up.
  if (planCtx && planCtx.state && planCtx.planDayId) {
    for (const k of dates) {
      if (planDayFor(planCtx.state, k, defaultCycleAnchor())?.day?.id !== planCtx.planDayId) continue;
      const found = sessionAt(k);
      if (found) return { ...found, sameDay: true };
    }
  }
  for (const k of dates) {
    const found = sessionAt(k);
    if (found) return { ...found, sameDay: false };
  }
  return null;
}

/* Whole-number days between two date keys (b - a). */
export function daysBetweenKeys(a, b) {
  const da = parseAnchor(a);
  const db = parseAnchor(b);
  if (!da || !db) return null;
  return Math.round((db - da) / 86400000);
}

/* Set list -> "80x8, 80x5, 80x3", collapsing runs of identical sets to
 * "80x8 x3" so straight sets stay short while a pyramid keeps its shape. */
export function formatSetShape(sets, maxGroups = 4) {
  if (!sets || !sets.length) return '';
  const groups = [];
  for (const s of sets) {
    const last = groups[groups.length - 1];
    if (last && last.weight === s.weight && last.reps === s.reps) last.count += 1;
    else groups.push({ weight: s.weight, reps: s.reps, count: 1 });
  }
  const shown = groups.slice(0, maxGroups).map(g => {
    const base = `${g.weight || 0}x${g.reps || 0}`;
    return g.count > 1 ? `${base} x${g.count}` : base;
  });
  if (groups.length > maxGroups) shown.push(`+${groups.length - maxGroups}`);
  return shown.join(', ');
}

export function sessionVolume(sets) {
  return (sets || []).reduce((sum, s) => sum + (s.weight || 0) * (s.reps || 0), 0);
}

/* All-time best for an exercise. Returns highest weight ever logged, plus the
 * highest reps achieved at that weight. Excludes one (date, index) pair so a
 * just-completed set can compare against history without comparing to itself. */
export function findExerciseAllTimeBest(logs, exerciseName, excludeKey, excludeIdx) {
  let bestWeight = 0;
  let bestRepsAtBest = 0;
  Object.entries(logs || {}).forEach(([k, dayLog]) => {
    const sets = dayLog?.[exerciseName] || [];
    sets.forEach((s, i) => {
      if (!s?.done) return;
      if (k === excludeKey && i === excludeIdx) return;
      const w = Number(s.weight) || 0;
      const r = Number(s.reps) || 0;
      if (w > bestWeight) { bestWeight = w; bestRepsAtBest = r; }
      else if (w === bestWeight && r > bestRepsAtBest) { bestRepsAtBest = r; }
    });
  });
  return { weight: bestWeight, reps: bestRepsAtBest };
}

/* Build a per-session top-set series for one exercise. Sorted by date asc.
 * Each point: { date, label, topWeight, volume, reps }. */
export function buildExerciseProgressSeries(logs, exerciseName) {
  const points = [];
  Object.keys(logs || {}).sort().forEach(k => {
    const sets = logs[k]?.[exerciseName] || [];
    const done = sets.filter(s => s?.done && (Number(s.weight) || 0) > 0);
    if (!done.length) return;
    const top = done.reduce((a, b) => ((Number(b.weight) || 0) > (Number(a.weight) || 0) ? b : a));
    const volume = done.reduce((sum, s) => sum + (Number(s.weight) || 0) * (Number(s.reps) || 0), 0);
    const d = new Date(`${k}T12:00:00`);
    points.push({
      date: k,
      label: d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' }),
      topWeight: Number(top.weight) || 0,
      reps: Number(top.reps) || 0,
      volume: Math.round(volume),
    });
  });
  return points;
}

export function calculateStreak(logs) {
  let n = 0; const c = new Date();
  while (hasSessionData(logs[dateKey(c)])) { n += 1; c.setDate(c.getDate() - 1); }
  return n;
}
